import { useRef, useEffect } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
  SIZE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import CloseLgIcon from '@skyscanner/backpack-web/bpk-component-icon/lg/close';
import MinusLgIcon from '@skyscanner/backpack-web/bpk-component-icon/lg/minus';
import PlusLgIcon from '@skyscanner/backpack-web/bpk-component-icon/lg/plus';
import BpkMap, {
  withGoogleMapsScript,
} from '@skyscanner/backpack-web/bpk-component-map';
import BpkTooltip, {
  TOOLTIP_TYPES,
} from '@skyscanner/backpack-web/bpk-component-tooltip';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import NoneStyleButton from '../../common/NoneStyleButton';
import { withGoogleMaps } from '../../contexts/google-maps-context';
import { withI18n } from '../../contexts/i18n';
import CenteredSpinner from '../CenteredSpinner';
import ZoomControl from '../ZoomControl';

import type { MapWrapperProps } from '../../types';
import type { GoogleMaps } from '@shared/types/client-side-params';
import type { ExtendedMap } from '@shared/types/frontend/google-maps';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './Map.module.scss';

const cls = cssModules(STYLES);

export type MapProps = {
  i18n: I18nService;
  googleMaps: GoogleMaps;
  enableNewMapControls?: boolean;
  showChatBoxPOCControls?: boolean;
  onCloseMap?: () => void;
  onExpand?: () => void;
  onCollapse?: () => void;
} & MapWrapperProps;

const zoomInBtnSelector = '.gm-bundled-control li:nth-child(5) > button';
const zoomOutBtnSelector = '.gm-bundled-control li:nth-child(6) > button';
const zoomBtnClickBy = (selector: string) => {
  const zoomBtns = document.querySelectorAll(
    selector,
  ) as NodeListOf<HTMLButtonElement>;
  const zoomBtnActive = zoomBtns[zoomBtns.length - 1];
  zoomBtnActive?.click();
};

const BpkMapWithScript = withGoogleMapsScript(BpkMap);

const Map = ({
  children,
  className,
  controlContainerClassName,
  disableDragging = false,
  enableNewMapControls = false,
  googleMaps,
  i18n,
  mapRef,
  onCloseMap,
  showChatBoxPOCControls = false,
  showControls,
  ...rest
}: MapProps) => {
  const targetZoomIn = useRef(null);
  const targetZoomOut = useRef(null);
  const mapInstance = useRef<ExtendedMap | null>(null);
  const zoomInLabel = i18n.translate('Map_tooltip_zoomIn');
  const zoomOutLabel = i18n.translate('Map_tooltip_zoomOut');
  const closeLabel = i18n.translate('Map_tooltip_close');

  const handleMapRef = (map: ExtendedMap) => {
    mapInstance.current = map;
    if (mapRef) {
      mapRef(map);
    }
  };

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.setOptions({
        draggable: !disableDragging,
      });
    }
  }, [disableDragging]);

  const mapOptions = {
    draggable: !disableDragging,
  };

  return (
    <BpkMapWithScript
      {...rest}
      googleMapsApiKey={googleMaps.apiKey}
      showControls={showControls}
      loadingElement={<CenteredSpinner />}
      className={cls(className, showControls ? 'Map__HideDefaultControl' : '')}
      // Hide hotel markers provided by Google to avoid conflict with our custom ones
      mapOptionStyles={[
        {
          featureType: 'poi.business.lodging',
          elementType: 'all',
          stylers: [{ visibility: 'off' }],
        },
      ]}
      options={mapOptions}
      mapRef={handleMapRef}
    >
      {children}
      {showControls && enableNewMapControls && !showChatBoxPOCControls && (
        <div
          className={cls('Map__controlContainer', controlContainerClassName)}
        >
          <BpkButtonV2
            className={cls('Map__controlIconContainerClose')}
            iconOnly
            aria-label={closeLabel}
            size={SIZE_TYPES.large}
            type={BUTTON_TYPES.primaryOnDark}
            onClick={() => {
              onCloseMap?.();
            }}
          >
            <CloseLgIcon className={cls('Map__controlIcon')} />
          </BpkButtonV2>
          <div className={cls('Map__controlIconContainerZoom')}>
            <BpkButtonV2
              className={cls('Map__controlIconContainer')}
              iconOnly
              size={SIZE_TYPES.large}
              aria-label={zoomInLabel}
              type={BUTTON_TYPES.primaryOnDark}
              onClick={() => {
                zoomBtnClickBy(zoomInBtnSelector);
              }}
            >
              <PlusLgIcon className={cls('Map__controlIcon')} />
            </BpkButtonV2>
            <BpkButtonV2
              className={cls('Map__controlIconContainer')}
              size={SIZE_TYPES.large}
              iconOnly
              aria-label={zoomOutLabel}
              type={BUTTON_TYPES.primaryOnDark}
              onClick={() => {
                zoomBtnClickBy(zoomOutBtnSelector);
              }}
            >
              <MinusLgIcon className={cls('Map__controlIcon')} />
            </BpkButtonV2>
          </div>
        </div>
      )}
      {showControls && !enableNewMapControls && !showChatBoxPOCControls && (
        <div
          className={cls(
            'Map__controlContainer--old',
            controlContainerClassName,
          )}
        >
          <BpkTooltip
            ariaLabel={zoomInLabel}
            id="map-tooltip-zoomin"
            placement="left"
            type={TOOLTIP_TYPES.dark}
            target={
              <NoneStyleButton
                className={cls('Map__controlIconContainer--old')}
                onClick={() => {
                  zoomBtnClickBy(zoomInBtnSelector);
                }}
                ref={targetZoomIn}
              >
                <PlusLgIcon className={cls('Map__controlIcon')} />
              </NoneStyleButton>
            }
          >
            {zoomInLabel}
          </BpkTooltip>
          <div className={cls('Map__divider')} />
          <BpkTooltip
            ariaLabel={zoomOutLabel}
            id="map-tooltip-zoomout"
            placement="left"
            type={TOOLTIP_TYPES.dark}
            target={
              <NoneStyleButton
                className={cls('Map__controlIconContainer--old')}
                onClick={() => {
                  zoomBtnClickBy(zoomOutBtnSelector);
                }}
                ref={targetZoomOut}
              >
                <MinusLgIcon className={cls('Map__controlIcon')} />
              </NoneStyleButton>
            }
          >
            {zoomOutLabel}
          </BpkTooltip>
        </div>
      )}
      {showChatBoxPOCControls && (
        <ZoomControl
          onExpand={() => zoomBtnClickBy(zoomInBtnSelector)}
          onCollapse={() => zoomBtnClickBy(zoomOutBtnSelector)}
          zoomInLabel={zoomInLabel}
          zoomOutLabel={zoomOutLabel}
        />
      )}
    </BpkMapWithScript>
  );
};

export default withI18n(withGoogleMaps(Map));
